import { ChartComponent } from '..'
import { Axis, createAxis } from './axes'
import { createBackgroundGradient } from './backgroundGradient'
import { Chart } from './chart'
import { CircleHighlight, createCircleHightlights } from './circle-hightlight'
import { createHighlightLine, createLineBottomGradients, createLines } from './lines'
import { Ticks, createTicks, responsiveLabels } from './ticks'
import { createTooltip } from './tooltip'
import { calculateLimitsFromValues, combineLimits } from './limits'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'
import { DataSource } from '@/components/AnalyticsDashboard/composables/useDataSources'
import { ColorScaleInterpolate, ColorScaleStep, getColorForValue } from '@/utils/colorScale'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { LegendComponent } from '../../LegendBase'
import { createErrorText } from './utils'

const DEFAULT_HORIZONTAL_AXIS: Axis<'top' | 'bottom'> = {
  position: 'bottom',
  showTicks: false,
  showLine: false
}

const DEFAULT_VERTICAL_AXIS: Axis<'left' | 'right'> = {
  position: 'left',
  showTicks: false,
  showLine: false,
  lines: true
}

export type LineChartValue = {
  x: number
  y: number
}

export type LineFormat = {
  color?: string
  width?: number
  style?: 'solid' | 'dashed' | 'dotted'
}

export type LineChart = {
  verticalAxis?: Partial<Axis<'left' | 'right'>>
  horizontalAxis?: Partial<Axis<'top' | 'bottom'>>
  yTicks?: Ticks
  xTicks?: Ticks
  xFormat: FormatOptions
  yFormat: FormatOptions
  timeFormat?: FormatOptions
  xTooltipFormat?: FormatOptions
  colorScale?: string
  gradientBelow?: boolean
  dataSources: string[]
  labels: LocaleString[]
  lineFormats: LineFormat[]
  missingDataMessage: LocaleString
}

const getLineChart = (
  chart: Chart<'line'>,
  dataSource: Record<string, DataSource<LineChartValue[]> | undefined>
): LineChartValue[][] =>
  chart.dataSources
    .map((source) => dataSource[source]?.data)
    .filter((data) => data !== undefined && data.length !== 0) as LineChartValue[][]

export function createLineChart(
  chart: Chart<'line'>,
  dataSources: Record<string, DataSource<LineChartValue[]> | undefined>,
  viewport: { width: number; height: number },
  locale: string
): ChartComponent[] | undefined {
  if (chart.dataSources.length === 0) return undefined
  if (!chart.dataSources.every((source) => dataSources[source]?.data))
    return createErrorText(chart.missingDataMessage, locale)
  if (chart.colorScale && !dataSources[chart.colorScale]?.data) return undefined
  const labels = chart.labels.map((l) => l[locale])

  const lines = getLineChart(chart, dataSources)

  const horizontalAxis: Axis<'top' | 'bottom'> = {
    ...DEFAULT_HORIZONTAL_AXIS,
    ...chart.horizontalAxis
  }
  const verticalAxis: Axis<'left' | 'right'> = {
    ...DEFAULT_VERTICAL_AXIS,
    ...chart.verticalAxis
  }
  const xValues = lines.map((p) => p.map((p) => p.x))
  const yValues = lines.map((p) => p.map((p) => p.y))
  const dataLimits = calculateLimitsFromValues(xValues.flat(), yValues.flat())
  const horizontalTicks = responsiveLabels(
    createTicks(chart.xTicks ?? 'auto', dataLimits.x, xValues[0], chart.xFormat, locale),
    viewport.width
  )
  const verticalTicks = createTicks(
    chart.yTicks ?? 'auto',
    dataLimits.y,
    yValues[0],
    chart.yFormat,
    locale
  )
  const tickLimits = calculateLimitsFromValues(horizontalTicks.values, verticalTicks.values)
  const limits = combineLimits([dataLimits, tickLimits])

  const colorScale = (chart.colorScale ? dataSources[chart.colorScale]?.data : undefined) as
    | ColorScaleInterpolate
    | ColorScaleStep
    | undefined

  const points = lines.map((l) => l.map((p) => [p.x, p.y] as [number, number]))
  const colors = points.map((p, i) => p.map(() => chart.lineFormats[i].color ?? 'red'))
  const filteredPoints = points.map(
    (p) => p.filter((p) => p[1] !== undefined) as [number, number][]
  )
  if (filteredPoints.some((p) => p.length === 0))
    return createErrorText(chart.missingDataMessage, locale)

  const highlights: CircleHighlight[] = filteredPoints.map(
    (p, i): CircleHighlight => ({
      label: labels[i],
      color: colors[i] ?? p.map(([, y]) => getColorForValue(colorScale!, y).toHex()),
      positions: p,
      pattern:
        chart.lineFormats[i].style !== 'solid'
          ? {
              type: 'horizontal-stripes',
              color: colors[i][0],
              strokeWidth: 5
            }
          : undefined,
      colorScale,
      visible: true
    })
  )

  return [
    ...createHighlightLine(limits.x, 'vertical'),
    ...createTooltip(limits, chart.yFormat, chart.xTooltipFormat),
    ...(colorScale ? createBackgroundGradient(colorScale, limits, 'vertical', 0.1) : []),
    ...createAxis(horizontalAxis, horizontalTicks, limits.x),
    ...createAxis(verticalAxis, verticalTicks, limits.y),
    ...createLines(labels, chart.lineFormats, points, limits, colorScale),
    ...(chart.gradientBelow
      ? createLineBottomGradients(
          labels,
          colors.map((x) => x[0]),
          points,
          limits
        )
      : []),
    ...createCircleHightlights(highlights, limits)
  ]
}

export function createTimeSeriesLegendComponents(
  chart: Chart<'timeseries'>,
  locale: string
): LegendComponent[] {
  const toCheckboxStyle = (style?: 'solid' | 'dashed' | 'dotted'): 'fill' | 'outline' => {
    if (style === 'dashed' || style === 'dotted') return 'outline'
    return 'fill'
  }

  return chart.labels.map((label, i) => ({
    type: 'checkbox',
    label: label[locale],
    styling: toCheckboxStyle(chart.lineFormats[i].style),
    color: chart.lineFormats[i].color,
    initial: true
  }))
}
